/**
 * MobileAddExterneModal
 *
 * MUI full-screen Dialog replacement for AddExterneModal (PrimeReact Dialog)
 * for mobile devices (isMobile === 2).
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography,
 *   Autocomplete, TextField, Button, Box, Slide, Dialog (confirm)
 */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Slide,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Redux
import {
  addCollab,
  civiliteOptions,
  getAllCollabs,
} from "../../../components/collabs/collabsSlice";
import { uploadAsset } from "../../../assets/assetsSlice";

// Shared utilities
import Langs, { Localize } from "../../../app/lang/langs";
import {
  showErrorToast,
  showSuccessToast,
  validateEmail,
} from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Slide-up transition                                                */
/* ------------------------------------------------------------------ */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const inputSx = {
  "& input": { fontSize: "16px" },
  "& .MuiInputBase-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
  },
  "& input::placeholder": { opacity: 0.45 },
};

const labelSx = {
  fontWeight: 600,
  fontSize: "0.82rem",
  color: "text.secondary",
  mb: 0.75,
  letterSpacing: "-0.01em",
  textTransform: "uppercase",
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileAddExterneModal(props) {
  const dispatch = useDispatch();

  // Redux state
  const subcontractors = useSelector((state) => state.clients.subcontractors);

  // Local state
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [showConfirmAddDuplicate, setShowConfirmAddDuplicate] = useState(null);

  /* ---- helpers ---- */

  const subcontractorOptions = (subcontractors || []).map((s) => ({
    label: s.name,
    value: s.id,
  }));

  const handleChange = (name, value) => {
    if (name == "sortie_date" && value) {
      value = moment(value).endOf("day").toDate();
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    setFiles(e.target.files);
  };

  const validateForm = () => {
    const mandatoryFields = [
      "id_subcontractor",
      "title",
      "email",
      "nom",
      "prenom",
      "mobile_number",
      "title",
      "postal_code",
      "city",
      "entree_date",
    ];

    const newErrors = {};
    for (let i = 0; i < mandatoryFields.length; i++) {
      if (!formData[mandatoryFields[i]]) newErrors[mandatoryFields[i]] = true;
    }
    if (formData.email_perso && !validateEmail(formData.email_perso))
      newErrors.email_perso = true;
    if (!validateEmail(formData.email)) newErrors.email = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (confirmDuplicate) => {
    if (!validateForm()) {
      return showErrorToast(
        Localize("Veuillez remplir tous les champs obligatoires.")
      );
    }
    try {
      if (confirmDuplicate) {
        formData.confirm_duplicate = true;
      }
      dispatch(addCollab(formData)).then((actionResponse) => {
        if (addCollab.fulfilled.match(actionResponse)) {
          if (
            actionResponse.payload.responseData &&
            actionResponse.payload.responseData.status
          ) {
            if (actionResponse.payload.responseData.status == 2) {
              return showErrorToast(
                Localize("Email") +
                  ` ${formData.email} ` +
                  Localize("déjà existant")
              );
            }
            if (actionResponse.payload.responseData.status == 3) {
              setShowConfirmAddDuplicate(
                actionResponse.payload.responseData.collabs
              );
              return;
            }
            setShowConfirmAddDuplicate(false);
            let idCollab = actionResponse.payload.responseData.insert_id;
            Array.from(files).map((file) =>
              dispatch(
                uploadAsset({
                  tableName: "collabs",
                  idParent: idCollab,
                  filename: file.name,
                  file,
                })
              )
            );
            showSuccessToast(
              Localize("Collaborateur ajouté avec succès.")
            );
            setFormData({});
            setFiles([]);
            props.onHide();
            dispatch(getAllCollabs());
          }
        }
      });
    } catch (error) {
      showErrorToast(Localize("Erreur lors de l'ajout du collaborateur."));
    }
  };

  const handleClose = () => {
    props.onHide();
    setFormData({});
    setFiles([]);
  };

  /* ---- date helper ---- */
  const toInputDate = (d) => {
    if (!d) return "";
    return moment(d).format("YYYY-MM-DD");
  };

  const todayStr = moment().format("YYYY-MM-DD");

  /* ---- text fields config ---- */
  const textFields = [
    { label: "FAMILY_NAME", name: "nom", placeholder: "Nom" },
    { label: "NAME", name: "prenom", placeholder: "Prénom" },
    { label: "CITY", name: "city", placeholder: "Ville" },
    { label: "CODE_POSTAL", name: "postal_code", placeholder: "Code postale" },
    {
      label: "EMAIL",
      name: "email",
      placeholder: "example@groupe-vital.com",
      type: "email",
    },
    {
      label: "EMAIL_PERSO",
      name: "email_perso",
      placeholder: "example@domain.com",
      type: "email",
    },
    { label: "TEL_FIXE", name: "telephone_number", placeholder: "Tel", type: "tel" },
    { label: "MOBILE_NUMBER", name: "mobile_number", placeholder: "Mobile", type: "tel" },
  ];

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <>
      <Dialog
        fullScreen
        open={!!props.visible}
        onClose={handleClose}
        TransitionComponent={Transition}
        sx={{
          zIndex: 1400,
          "& .MuiDialog-paper": {
            background: "var(--surface-ground, #f4f6f8)",
          },
        }}
      >
        {/* ---------- AppBar ---------- */}
        <AppBar
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "rgba(255,255,255,0.82)",
            color: "text.primary",
            boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Toolbar sx={{ py: 0.5 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: "12px",
                transition: TRANSITION_SMOOTH,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.06)",
                  transform: "scale(1.08)",
                },
                "&:active": { transform: "scale(0.95)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                ml: 1.5,
                flex: 1,
                fontWeight: 600,
                fontSize: "1.15rem",
                letterSpacing: "-0.02em",
              }}
              variant="h6"
            >
              <Langs str="ADD_EXTERNE" />
            </Typography>
          </Toolbar>
        </AppBar>

        {/* ---------- Form content ---------- */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            pt: 2.5,
            pb: "100px",
            "@keyframes fadeSlideIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
          }}
        >
          <Box
            sx={{
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.45)",
              boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
              p: 2.5,
              transition: TRANSITION_SMOOTH,
            }}
          >
            {/* 1. Subcontractor */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="Subcontractor" /> *
              </Typography>
              <Autocomplete
                options={subcontractorOptions}
                getOptionLabel={(o) => o.label || ""}
                value={
                  subcontractorOptions.find(
                    (o) => o.value === formData.id_subcontractor
                  ) || null
                }
                onChange={(_, opt) =>
                  handleChange("id_subcontractor", opt ? opt.value : null)
                }
                isOptionEqualToValue={(o, v) => o.value === v.value}
                slotProps={{ popper: { sx: { zIndex: 1500 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={Localize("Subcontractor")}
                    size="small"
                    error={!!errors.id_subcontractor}
                    sx={inputSx}
                  />
                )}
              />
            </Box>

            {/* 2. Date de naissance */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="DATE_NAISSANCE" />
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toInputDate(formData.date_naissance)}
                onChange={(e) =>
                  handleChange(
                    "date_naissance",
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
                inputProps={{ max: todayStr }}
                error={!!errors.date_naissance}
                sx={inputSx}
              />
            </Box>

            {/* 3. Civilité */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="CIVILITY" /> *
              </Typography>
              <Autocomplete
                options={civiliteOptions}
                getOptionLabel={(o) => o.label || ""}
                value={
                  civiliteOptions.find((o) => o.value === formData.title) || null
                }
                onChange={(_, opt) =>
                  handleChange("title", opt ? opt.value : null)
                }
                isOptionEqualToValue={(o, v) => o.value === v.value}
                slotProps={{ popper: { sx: { zIndex: 1500 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={Localize("Title")}
                    size="small"
                    error={!!errors.title}
                    sx={inputSx}
                  />
                )}
              />
            </Box>

            {/* 4-11. Text fields */}
            {textFields.map((field) => (
              <Box sx={{ mb: 2.5 }} key={field.name}>
                <Typography sx={labelSx}>
                  <Langs str={field.label} />{" "}
                  {[
                    "nom",
                    "prenom",
                    "city",
                    "postal_code",
                    "email",
                    "mobile_number",
                  ].includes(field.name)
                    ? "*"
                    : ""}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  error={!!errors[field.name]}
                  inputProps={{ maxLength: 255 }}
                  sx={inputSx}
                />
              </Box>
            ))}

            {/* 12. Date d'entrée */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="ENTRY_DATE" /> *
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toInputDate(formData.entree_date)}
                onChange={(e) =>
                  handleChange(
                    "entree_date",
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
                error={!!errors.entree_date}
                sx={inputSx}
              />
            </Box>

            {/* 13. Date de sortie */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="SORTIE_DATE" />
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toInputDate(formData.sortie_date)}
                onChange={(e) =>
                  handleChange(
                    "sortie_date",
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
                disabled={!formData.entree_date}
                inputProps={{
                  min: toInputDate(formData.entree_date),
                }}
                error={!!errors.sortie_date}
                sx={inputSx}
              />
            </Box>

            {/* 14. Date début CP */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="Date début CP" />
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toInputDate(formData.date_debut_cp)}
                onChange={(e) =>
                  handleChange(
                    "date_debut_cp",
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
                error={!!errors.date_debut_cp}
                sx={inputSx}
              />
            </Box>

            {/* 15. Date fin CP */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="Date fin CP" />
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toInputDate(formData.date_fin_cp)}
                onChange={(e) =>
                  handleChange(
                    "date_fin_cp",
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
                disabled={!formData.date_debut_cp}
                inputProps={{
                  min: toInputDate(formData.date_debut_cp),
                }}
                error={!!errors.date_fin_cp}
                sx={inputSx}
              />
            </Box>

            {/* 16. TJM achat */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="TJM achat" />
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="0000"
                value={formData.tjm_achat ?? ""}
                onChange={(e) =>
                  handleChange(
                    "tjm_achat",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                inputProps={{ min: 0 }}
                sx={inputSx}
              />
            </Box>

            {/* 17. TJM vente */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="TJM vente" />
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="0000"
                value={formData.tjm_vente ?? ""}
                onChange={(e) =>
                  handleChange(
                    "tjm_vente",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                inputProps={{ min: 0 }}
                sx={inputSx}
              />
            </Box>

            {/* 18. Upload files */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={labelSx}>
                <Langs str="UPLOAD_FILES" />
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{
                  minHeight: 48,
                  borderRadius: BORDER_RADIUS_INPUT,
                  textTransform: "none",
                  fontWeight: 500,
                  justifyContent: "flex-start",
                  color: "text.secondary",
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: TRANSITION_SMOOTH,
                  "&:hover": {
                    borderColor: "rgba(0,0,0,0.25)",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  },
                }}
              >
                {files.length > 0
                  ? `${files.length} fichier(s)`
                  : Localize("UPLOAD_FILES")}
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  hidden
                />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ---------- Sticky footer ---------- */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderTop: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
            px: 2.5,
            py: 1.5,
            display: "flex",
            gap: 1.5,
            zIndex: 1,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={handleClose}
            sx={{
              minHeight: 52,
              borderRadius: BORDER_RADIUS_BUTTON,
              textTransform: "none",
              fontWeight: 600,
              transition: TRANSITION_SMOOTH,
              "&:hover": { transform: "scale(1.02)" },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <Langs str="CANCEL" />
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleSubmit()}
            sx={{
              minHeight: 52,
              borderRadius: BORDER_RADIUS_BUTTON,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#ff9015",
              boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                bgcolor: "#e67e00",
                transform: "scale(1.02)",
                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
              },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <Langs str="ADD" />
          </Button>
        </Box>
      </Dialog>

      {/* ---------- Duplicate confirmation dialog ---------- */}
      <Dialog
        open={Boolean(showConfirmAddDuplicate)}
        onClose={() => setShowConfirmAddDuplicate(null)}
        sx={{
          zIndex: 1500,
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            backdropFilter: "blur(12px)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, letterSpacing: "-0.02em" }}>
          <Langs str="ADD_DUPLICATE_COLLAB" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Langs
              str="ADD_DUPLICATE_COLLAB_MSG"
              params={{ count: showConfirmAddDuplicate?.length }}
            />
          </DialogContentText>
          <Box sx={{ mt: 1 }}>
            {showConfirmAddDuplicate
              ? showConfirmAddDuplicate.map((c, idx) => (
                  <Typography
                    key={idx}
                    sx={{ color: "error.main", fontSize: "0.875rem" }}
                  >
                    {c}
                  </Typography>
                ))
              : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setShowConfirmAddDuplicate(null)}
            sx={{
              textTransform: "none",
              minHeight: 44,
              borderRadius: "10px",
              transition: TRANSITION_SMOOTH,
            }}
          >
            <Langs str="CANCEL" />
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmit(true)}
            sx={{
              textTransform: "none",
              minHeight: 44,
              borderRadius: "10px",
              bgcolor: "#ff9015",
              boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                bgcolor: "#e67e00",
                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
              },
            }}
          >
            <Langs str="CONFIRM" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
